import React, { useState } from 'react';
import { Calendar as CalendarIcon, Clock, Video, Image, Mic, ShoppingBag, Sparkles, TrendingUp, Users, Gift, MessageCircle, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
// Sample data for calendar
const events = [{
  id: 1,
  title: 'Premium Lingerie Try-On',
  date: '2023-06-05',
  time: '19:00',
  type: 'video',
  isNSFW: true,
  isPremium: true,
  isScheduled: true
}, {
  id: 2,
  title: 'Morning Routine',
  date: '2023-06-07',
  time: '09:00',
  type: 'video',
  isNSFW: true,
  isPremium: false,
  isScheduled: true
}, {
  id: 3,
  title: 'Live Q&A Session',
  date: '2023-06-10',
  time: '20:00',
  type: 'live',
  isNSFW: false,
  isPremium: false,
  isScheduled: true
}, {
  id: 4,
  title: 'New Toy Unboxing',
  date: '2023-06-12',
  time: '18:00',
  type: 'video',
  isNSFW: true,
  isPremium: true,
  isScheduled: true
}, {
  id: 5,
  title: 'Workout Session',
  date: '2023-06-15',
  time: '16:00',
  type: 'video',
  isNSFW: false,
  isPremium: false,
  isScheduled: true
}, {
  id: 6,
  title: 'Lingerie Promo Campaign',
  date: '2023-06-08',
  time: 'All day',
  type: 'campaign',
  isNSFW: true,
  isPremium: false,
  isScheduled: true
}, {
  id: 7,
  title: 'ASMR Whispers',
  date: '2023-06-17',
  time: '22:00',
  type: 'audio',
  isNSFW: true,
  isPremium: true,
  isScheduled: false
}, {
  id: 8,
  title: 'Interactive Toy Session',
  date: '2023-06-20',
  time: '21:00',
  type: 'live',
  isNSFW: true,
  isPremium: true,
  isScheduled: false
}];
const trendingTopics = [{
  id: 1,
  name: 'Summer Vacation',
  growth: '+45%'
}, {
  id: 2,
  name: 'Beach Outfits',
  growth: '+38%'
}, {
  id: 3,
  name: 'Shower Routines',
  growth: '+32%'
}, {
  id: 4,
  name: 'Interactive Toys',
  growth: '+28%'
}, {
  id: 5,
  name: 'ASMR Roleplay',
  growth: '+25%'
}];
const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export function ContentPlanningSection() {
  const [currentMonth, setCurrentMonth] = useState(5); // June (0-indexed)
  const [currentYear, setCurrentYear] = useState(2023);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };
  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };
  const getEventsForDate = (date: string) => {
    return events.filter(event => event.date === date);
  };
  const handleDateClick = (date: string) => {
    setSelectedDate(date);
  };
  return <div className="w-full space-y-6" data-id="element-3897">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3898">
        {/* Calendar */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm md:col-span-2" data-id="element-3899">
          <div className="flex items-center justify-between mb-4" data-id="element-3900">
            <h3 className="text-lg font-semibold" data-id="element-3901">Content Calendar</h3>
            <div className="flex items-center space-x-2" data-id="element-3902">
              <button className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700" onClick={prevMonth} data-id="element-3903">
                <ChevronLeft className="w-5 h-5" data-id="element-3904" />
              </button>
              <span className="text-sm font-medium" data-id="element-3905">
                {months[currentMonth]} {currentYear}
              </span>
              <button className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700" onClick={nextMonth} data-id="element-3906">
                <ChevronRight className="w-5 h-5" data-id="element-3907" />
              </button>
            </div>
          </div>
          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1" data-id="element-3908">
            {weekdays.map((day, index) => <div key={index} className="text-center text-sm font-medium py-2" data-id="element-3909">
                {day}
              </div>)}
            {/* Empty cells for days before the first day of month */}
            {Array.from({
            length: firstDayOfMonth
          }).map((_, index) => <div key={`empty-${index}`} className="h-24 p-1 bg-gray-50 dark:bg-gray-700/50 rounded-md" data-id="element-3910"></div>)}
            {/* Calendar days */}
            {Array.from({
            length: daysInMonth
          }).map((_, index) => {
            const day = index + 1;
            const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayEvents = getEventsForDate(dateStr);
            const isSelected = dateStr === selectedDate;
            return <div key={`day-${day}`} className={`h-24 p-1 bg-gray-50 dark:bg-gray-700/50 rounded-md overflow-hidden hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer ${isSelected ? 'ring-2 ring-purple-500' : ''}`} onClick={() => handleDateClick(dateStr)} data-id="element-3911">
                  <div className="text-right text-sm font-medium p-1" data-id="element-3912">
                    {day}
                  </div>
                  <div className="space-y-1" data-id="element-3913">
                    {dayEvents.slice(0, 2).map(event => <div key={event.id} className={`text-xs px-1 py-0.5 rounded truncate ${event.type === 'live' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200' : event.type === 'campaign' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200' : event.isPremium ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200' : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'}`} data-id="element-3914">
                        {event.title}
                      </div>)}
                    {dayEvents.length > 2 && <div className="text-xs text-center text-gray-500 dark:text-gray-400" data-id="element-3915">
                        +{dayEvents.length - 2} more
                      </div>}
                  </div>
                </div>;
          })}
          </div>
        </div>
        {/* Optimal Posting Time */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-id="element-3916">
          <h3 className="text-lg font-semibold mb-4" data-id="element-3917">Optimal Posting Time</h3>
          <div className="flex justify-center mb-4" data-id="element-3918">
            <div className="w-32 h-32 rounded-full border-8 border-purple-200 dark:border-purple-900/30 flex items-center justify-center relative" data-id="element-3919">
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-3920">
                <div className="text-center" data-id="element-3921">
                  <h4 className="text-2xl font-bold" data-id="element-3922">9PM</h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3923">
                    Best Time
                  </p>
                </div>
              </div>
            </div>
          </div>
          <h4 className="text-md font-medium mb-2" data-id="element-3924">
            Audience Activity Heatmap
          </h4>
          <div className="grid grid-cols-7 gap-1 mb-1" data-id="element-3925">
            {weekdays.map((day, index) => <div key={index} className="text-center text-xs" data-id="element-3926">
                {day[0]}
              </div>)}
          </div>
          <div className="grid grid-cols-7 gap-1" data-id="element-3927">
            {[...Array(24)].map((_, hour) => [...Array(7)].map((_, day) => {
            // Generate random intensity for the heatmap
            const intensity = Math.floor(Math.random() * 5);
            const bgColorClass = ['bg-purple-50 dark:bg-purple-900/10', 'bg-purple-100 dark:bg-purple-900/20', 'bg-purple-200 dark:bg-purple-900/30', 'bg-purple-300 dark:bg-purple-900/50', 'bg-purple-400 dark:bg-purple-900/70'][intensity];
            // Highlight the best time
            const isBestTime = hour === 21 && (day === 2 || day === 5);
            const borderClass = isBestTime ? 'ring-2 ring-purple-500' : '';
            return <div key={`cell-${hour}-${day}`} className={`h-2 rounded-sm ${bgColorClass} ${borderClass}`} title={`${hour}:00 on ${weekdays[day]}`} data-id="element-3928" />;
          }))}
          </div>
          <div className="mt-4" data-id="element-3929">
            <h4 className="text-md font-medium mb-2" data-id="element-3930">
              Recommended Posting Schedule
            </h4>
            <ul className="space-y-2" data-id="element-3931">
              <li className="flex items-center text-sm" data-id="element-3932">
                <Clock className="w-4 h-4 text-purple-500 mr-2" data-id="element-3933" />
                <span data-id="element-3934">
                  Post premium content on <strong data-id="element-3935">Tuesday</strong> at{' '}
                  <strong data-id="element-3936">9PM</strong>
                </span>
              </li>
              <li className="flex items-center text-sm" data-id="element-3937">
                <Clock className="w-4 h-4 text-purple-500 mr-2" data-id="element-3938" />
                <span data-id="element-3939">
                  Schedule lives on <strong data-id="element-3940">Friday</strong> at{' '}
                  <strong data-id="element-3941">9PM</strong>
                </span>
              </li>
              <li className="flex items-center text-sm" data-id="element-3942">
                <Clock className="w-4 h-4 text-purple-500 mr-2" data-id="element-3943" />
                <span data-id="element-3944">
                  Share casual content on <strong data-id="element-3945">Sunday</strong> at{' '}
                  <strong data-id="element-3946">11AM</strong>
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      {/* Event Details and Trend Assistant */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3947">
        {/* Event Details */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm md:col-span-2" data-id="element-3948">
          <h3 className="text-lg font-semibold mb-4" data-id="element-3949">
            {selectedDate ? `Content for ${new Date(selectedDate).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}` : 'Upcoming Content'}
          </h3>
          {selectedDate ? <>
              <div className="space-y-3" data-id="element-3950">
                {getEventsForDate(selectedDate).length > 0 ? getEventsForDate(selectedDate).map(event => <div key={event.id} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg flex items-start" data-id="element-3951">
                      <div className={`p-2 rounded-lg mr-3 ${event.type === 'video' ? 'bg-green-100 dark:bg-green-900/30' : event.type === 'live' ? 'bg-red-100 dark:bg-red-900/30' : event.type === 'audio' ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-purple-100 dark:bg-purple-900/30'}`} data-id="element-3952">
                        {event.type === 'video' && <Video className="w-5 h-5 text-green-600 dark:text-green-400" data-id="element-3953" />}
                        {event.type === 'live' && <Users className="w-5 h-5 text-red-600 dark:text-red-400" data-id="element-3954" />}
                        {event.type === 'audio' && <Mic className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-3955" />}
                        {event.type === 'campaign' && <ShoppingBag className="w-5 h-5 text-purple-600 dark:text-purple-400" data-id="element-3956" />}
                      </div>
                      <div className="flex-1" data-id="element-3957">
                        <div className="flex items-center justify-between" data-id="element-3958">
                          <h4 className="font-medium" data-id="element-3959">{event.title}</h4>
                          <div className="flex items-center space-x-1" data-id="element-3960">
                            {event.isNSFW && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200" data-id="element-3961">
                                NSFW
                              </span>}
                            {event.isPremium && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200" data-id="element-3962">
                                Premium
                              </span>}
                          </div>
                        </div>
                        <div className="flex items-center mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-3963">
                          <Clock className="w-4 h-4 mr-1" data-id="element-3964" />
                          <span data-id="element-3965">{event.time}</span>
                        </div>
                        <div className="flex items-center justify-between mt-3" data-id="element-3966">
                          <div className="flex items-center space-x-2" data-id="element-3967">
                            <button className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-600 rounded" data-id="element-3968">
                              Edit
                            </button>
                            <button className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-600 rounded" data-id="element-3969">
                              Delete
                            </button>
                          </div>
                          {event.isScheduled ? <span className="text-xs text-green-600 dark:text-green-400 flex items-center" data-id="element-3970">
                              <Clock className="w-3 h-3 mr-1" data-id="element-3971" />
                              Scheduled
                            </span> : <button className="px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded" data-id="element-3972">
                              Schedule
                            </button>}
                        </div>
                      </div>
                    </div>) : <div className="p-8 text-center" data-id="element-3973">
                    <CalendarIcon className="w-12 h-12 mx-auto text-gray-400 mb-3" data-id="element-3974" />
                    <h4 className="text-lg font-medium mb-1" data-id="element-3975">
                      No content scheduled
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4" data-id="element-3976">
                      Add new content to this date to start planning your
                      schedule.
                    </p>
                    <button className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg font-medium flex items-center justify-center mx-auto" data-id="element-3977">
                      <Plus className="w-4 h-4 mr-2" data-id="element-3978" />
                      Add New Content
                    </button>
                  </div>}
              </div>
              {getEventsForDate(selectedDate).length > 0 && <button className="mt-4 px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg font-medium flex items-center justify-center mx-auto" data-id="element-3979">
                  <Plus className="w-4 h-4 mr-2" data-id="element-3980" />
                  Add New Content
                </button>}
            </> : <div className="space-y-3" data-id="element-3981">
              {events.filter(e => e.isScheduled).slice(0, 4).map(event => <div key={event.id} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg flex items-start" data-id="element-3982">
                    <div className={`p-2 rounded-lg mr-3 ${event.type === 'video' ? 'bg-green-100 dark:bg-green-900/30' : event.type === 'live' ? 'bg-red-100 dark:bg-red-900/30' : event.type === 'audio' ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-purple-100 dark:bg-purple-900/30'}`} data-id="element-3983">
                      {event.type === 'video' && <Video className="w-5 h-5 text-green-600 dark:text-green-400" data-id="element-3984" />}
                      {event.type === 'live' && <Users className="w-5 h-5 text-red-600 dark:text-red-400" data-id="element-3985" />}
                      {event.type === 'audio' && <Mic className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-3986" />}
                      {event.type === 'campaign' && <ShoppingBag className="w-5 h-5 text-purple-600 dark:text-purple-400" data-id="element-3987" />}
                    </div>
                    <div className="flex-1" data-id="element-3988">
                      <div className="flex items-center justify-between" data-id="element-3989">
                        <h4 className="font-medium" data-id="element-3990">{event.title}</h4>
                        <div className="flex items-center space-x-1" data-id="element-3991">
                          {event.isNSFW && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200" data-id="element-3992">
                              NSFW
                            </span>}
                          {event.isPremium && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200" data-id="element-3993">
                              Premium
                            </span>}
                        </div>
                      </div>
                      <div className="flex items-center mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-3994">
                        <CalendarIcon className="w-4 h-4 mr-1" data-id="element-3995" />
                        <span data-id="element-3996">
                          {new Date(event.date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                  })}
                        </span>
                        <span className="mx-2" data-id="element-3997">•</span>
                        <Clock className="w-4 h-4 mr-1" data-id="element-3998" />
                        <span data-id="element-3999">{event.time}</span>
                      </div>
                    </div>
                  </div>)}
            </div>}
        </div>
        {/* Trend Assistant */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm" data-id="element-4000">
          <h3 className="text-lg font-semibold mb-4" data-id="element-4001">Trend Assistant</h3>
          <div className="mb-4" data-id="element-4002">
            <h4 className="text-md font-medium mb-2" data-id="element-4003">Trending Topics</h4>
            <ul className="space-y-2" data-id="element-4004">
              {trendingTopics.map(topic => <li key={topic.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg" data-id="element-4005">
                  <div className="flex items-center" data-id="element-4006">
                    <Sparkles className="w-4 h-4 text-yellow-500 mr-2" data-id="element-4007" />
                    <span className="text-sm" data-id="element-4008">{topic.name}</span>
                  </div>
                  <span className="text-xs text-green-500 font-medium flex items-center" data-id="element-4009">
                    <TrendingUp className="w-3 h-3 mr-1" data-id="element-4010" />
                    {topic.growth}
                  </span>
                </li>)}
            </ul>
          </div>
          <div className="mb-4" data-id="element-4011">
            <h4 className="text-md font-medium mb-2" data-id="element-4012">Creator Challenges</h4>
            <div className="p-3 bg-gradient-to-r from-pink-500/10 to-purple-500/10 dark:from-pink-900/30 dark:to-purple-900/30 border border-purple-200 dark:border-purple-800 rounded-lg" data-id="element-4013">
              <h5 className="font-medium text-purple-700 dark:text-purple-300 mb-1" data-id="element-4014">
                Summer Swimsuit Challenge
              </h5>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2" data-id="element-4015">
                Join the trending challenge and showcase your summer swimwear
                collection.
              </p>
              <div className="flex items-center text-xs text-gray-500 dark:text-gray-400" data-id="element-4016">
                <Users className="w-3 h-3 mr-1" data-id="element-4017" />
                <span data-id="element-4018">1,245 creators participating</span>
                <span className="mx-2" data-id="element-4019">•</span>
                <Gift className="w-3 h-3 mr-1" data-id="element-4020" />
                <span data-id="element-4021">$500 prize pool</span>
              </div>
              <button className="mt-2 w-full py-1 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded text-xs font-medium" data-id="element-4022">
                Join Challenge
              </button>
            </div>
          </div>
          <div data-id="element-4023">
            <h4 className="text-md font-medium mb-2" data-id="element-4024">Engagement Planner</h4>
            <ul className="space-y-2" data-id="element-4025">
              <li className="flex items-center p-2 bg-gray-50 dark:bg-gray-700 rounded-lg" data-id="element-4026">
                <MessageCircle className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0" data-id="element-4027" />
                <span className="text-sm" data-id="element-4028">
                  Schedule Q&A for Friday to boost engagement
                </span>
              </li>
              <li className="flex items-center p-2 bg-gray-50 dark:bg-gray-700 rounded-lg" data-id="element-4029">
                <MessageCircle className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0" data-id="element-4030" />
                <span className="text-sm" data-id="element-4031">
                  Pin top fan comments on newest posts
                </span>
              </li>
              <li className="flex items-center p-2 bg-gray-50 dark:bg-gray-700 rounded-lg" data-id="element-4032">
                <MessageCircle className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0" data-id="element-4033" />
                <span className="text-sm" data-id="element-4034">
                  Set up automated replies for common questions
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>;
}